import { ChevronLeft, ChevronRight } from 'lucide-react';
import { palette, spacing, transition } from '../../styles/tokens';
import type { PaginationControls } from '../../hooks/usePagination';
import { PAGE_SIZE_OPTIONS } from '../../hooks/usePagination';

interface PaginationFooterProps {
  pagination: PaginationControls;
  totalCount: number | null;
  loading?: boolean;
}

export function PaginationFooter({ pagination, totalCount, loading }: PaginationFooterProps) {
  const { page, pageSize, setPageSize, nextPage, prevPage, canPrev, canNext, totalPages } = pagination;

  const total = totalCount ?? 0;
  const totalP = totalPages(totalCount);
  const firstRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastRow = Math.min(page * pageSize, total);

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: `${spacing.md}px 0`,
      marginTop: spacing.sm,
      borderTop: `1px solid ${palette.g150}`,
      gap: spacing.lg,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
        <span style={{ fontSize: 12, color: palette.g500 }}>
          {totalCount == null
            ? 'Loading\u2026'
            : total === 0
              ? 'No results'
              : `${firstRow.toLocaleString()}\u2013${lastRow.toLocaleString()} of ${total.toLocaleString()}`
          }
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
          <span style={{ fontSize: 12, color: palette.g400 }}>Rows:</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
            disabled={loading}
            style={{
              padding: `${spacing.xxs}px ${spacing.xs}px`,
              borderRadius: 4,
              border: `1px solid ${palette.g200}`,
              backgroundColor: palette.white,
              fontSize: 12,
              fontFamily: 'inherit',
              cursor: loading ? 'wait' : 'pointer',
            }}
          >
            {PAGE_SIZE_OPTIONS.map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
        <span style={{ fontSize: 12, color: palette.g500 }}>
          Page {page} of {totalP}
        </span>
        <button
          onClick={prevPage}
          disabled={!canPrev || loading}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 32, height: 32, borderRadius: 6,
            border: `1px solid ${palette.g200}`, backgroundColor: palette.white,
            cursor: (!canPrev || loading) ? 'not-allowed' : 'pointer',
            opacity: (!canPrev || loading) ? 0.4 : 1,
            transition: `all ${transition.fast}`,
          }}
        >
          <ChevronLeft size={16} color={palette.g700} />
        </button>
        <button
          onClick={nextPage}
          disabled={!canNext(totalCount) || loading}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 32, height: 32, borderRadius: 6,
            border: `1px solid ${palette.g200}`, backgroundColor: palette.white,
            cursor: (!canNext(totalCount) || loading) ? 'not-allowed' : 'pointer',
            opacity: (!canNext(totalCount) || loading) ? 0.4 : 1,
            transition: `all ${transition.fast}`,
          }}
        >
          <ChevronRight size={16} color={palette.g700} />
        </button>
      </div>
    </div>
  );
}
